<script setup lang="ts">
import SeperatorImg from '@/assets/components/loggedBox/separator.png'
import {
  checkConnection,
  connectedGames,
  connectionCheckingLoading,
  exclusiveOffers
} from '@/components/goat/page/home/ConnectGameDialog'
import { defaultLocale } from '@/settings/langs'

const { gameOrder, games, storeHost, triggerApi } = siteUtils

if (!import.meta.env.ssr) {
  checkConnection(null)
}
const auth = useAuthStore()
const route = useRoute()
function handleAllDeals(gameKey: Games.GameKeyType) {
  const { gaAllDealsBtnClick } = gaUtils
  gaAllDealsBtnClick({ gs_game: gameKey })
  if (window?.location?.href) {
    const currentLocale = String(route.meta?.pageData?.locale || route.params?.locale || defaultLocale)
    const params = new URLSearchParams({ goat_token: auth.getToken, goat_email: auth.getEmail, flang: currentLocale })
    window.location.href = `${storeHost}/${games?.[gameKey]?.gameRoute || ''}?${params.toString()}`
  }
}

const benefitDialogVisibility = ref(false)

const orderedGames = computed(() => {
  return [...gameOrder].sort((a, b) => {
    return (connectedGames.value?.[b]?.id ? 1 : 0) - (connectedGames.value?.[a]?.id ? 1 : 0)
  })
})
const { gaConnectBtnClick } = gaUtils
function onConnectGameBtnClick(gameKey: Games.GameKeyLax) {
  gaConnectBtnClick({ gs_game: gameKey || '' })
  checkConnection(gameKey, true, { appendTo: `#connect-btn-${gameKey}` })
}
const { t } = useI18n()
const { debounce } = tools
const loadAllDebounced = debounce(() => {
  checkConnection(null)
}, 500)
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
  watch(triggerApi.triggerRef, () => {
    if (isMounted.value) {
      loadAllDebounced()
    }
  })
})
onUnmounted(() => {
  isMounted.value = false
})
</script>

<template>
  <div
    id="logged-box"
  >
    <div class="bg-[#061c21] bg-linear-to-b from-[#061c21] to-[#174851] px-4 big:px-5 pt-5 pb-10 md:pt-7 md:pb-12 big:pb-16 relative">
      <div class="text-white mx-auto text-center max-w-[280px] big:max-w-[355px] 2xl:max-w-[400px] font-medium leading-tight text-sm  big:text-base 2xl:text-lg py-8">
        {{ t('loggedBox.welcome') }} <a
          class="underline underline-offset-4 cursor-pointer"
          @click.prevent="benefitDialogVisibility = !benefitDialogVisibility"
        >{{ t('loggedBox.allBenefits') }}</a>
      </div>
      <img
        alt="separator"
        class="w-full absolute lef-0 right-0 bottom-0 translate-y-1/2 z-10"
        :src="SeperatorImg"
      >
    </div>
    <div
      v-for="gameKey in orderedGames"
      :key="gameKey"
      class="px-2 py-2 sm:px-4 bg-cover sm:pb-8 big:py-[4vh] 2xl:py-[8vh] relative text-white"
      :style="`background-image: url('${games?.[gameKey]?.sectionBg}');`"
    >
      <div class="pt-10 text-center flex justify-center items-center">
        <el-image
          :src="games?.[gameKey]?.logo"
          :alt="`logo ${games?.[gameKey]?.name}`"
          class="h-16 big:h-24"
          fit="contain"
        />
      </div>
      <transition
        name="el-fade-in"
        mode="out-in"
      >
        <div v-if="connectedGames?.[gameKey]?.id">
          <div class="flex flex-col items-center mt-8 mb-6 gap-5 px-4">
            <div class="flex flex-col items-center gap-5 w-full max-w-md">
              <div
                class="flex items-center gap-4 rounded-2xl border border-white/50 px-5 py-4 w-full shadow-sm"
                :style="`background-color: ${games[gameKey]?.cardColor}`"
              >
                <el-avatar
                  shape="square"
                  class="size-14 big:size-16 shrink-0 bg-transparent rounded-xl shadow-lg ring-2 ring-white"
                  :src="games[gameKey]?.icon"
                />
                <div class="flex flex-col min-w-0">
                  <div
                    v-if="connectedGames?.[gameKey]?.nick?.length"
                    class="font-black text-xl truncate drop-shadow"
                  >
                    {{ connectedGames?.[gameKey]?.nick }}
                  </div>
                  <div
                    v-if="connectedGames?.[gameKey]?.loyaltyLeagueLogo"
                    class="flex items-center gap-1.5 mt-1"
                  >
                    <img
                      :src="`${connectedGames[gameKey].loyaltyLeagueLogo.substring(0, 4) === 'http' ? '' : games?.[gameKey]?.imgStorageUrl}${connectedGames[gameKey].loyaltyLeagueLogo}`"
                      alt="logo league"
                      :title="connectedGames[gameKey].loyaltyLeague"
                      class="w-6 big:w-7"
                    >
                    <span
                      v-if="connectedGames[gameKey].loyaltyLeague"
                      class="text-xs font-semibold uppercase tracking-widest text-white/80"
                    >
                      {{ connectedGames[gameKey].loyaltyLeague }}
                    </span>
                  </div>
                </div>
              </div>
              <el-button
                color="#3ba6bc"
                class="text-white rounded-xl uppercase text-xl font-black w-full sm:w-4/5 px-4 tracking-wide mt-8 mb-8 mx-auto"
                size="large"
                style="--el-button-size: calc(3em + 20px)"
                @click="handleAllDeals(gameKey)"
              >
                <span class="flex items-center justify-center gap-2">
                  <svg
                    class="hidden big:inline-block shrink-0"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                    <line
                      x1="3"
                      y1="6"
                      x2="21"
                      y2="6"
                    />
                    <path d="M16 10a4 4 0 0 1-8 0" />
                  </svg>
                  {{ t('loggedBox.storeBtn', { game: games?.[gameKey]?.name ?? '' }) }}
                </span>
              </el-button>
            </div>
          </div>
        </div>
        <div
          v-else
          class="grid grid-cols-1 justify-items-center mt-8 mb-6 gap-y-7"
        >
          <div class="grow font-medium max-w-[95%] lg:max-w-80 mx-auto text-center sm:text-xl">
            <button
              class="cursor-pointer underline underline-offset-4 hover:decoration-2 active:decoration-auto"
              @click="onConnectGameBtnClick(gameKey)"
            >
              {{ t('loggedBox.connectAccount') }}
            </button> <br> {{ t('loggedBox.exclusiveGift') }}
          </div>
          <goat-page-home-exclusive-offer-tile
            :offer="exclusiveOffers[gameKey] ?? null"
            :game-key="gameKey"
            class="w-full max-w-md mb-5"
          >
            <template #button>
              <div class="text-center">
                <el-button
                  :id="`connect-btn-${gameKey}`"
                  color="#3ba6bc"
                  class="text-white rounded-xl uppercase text-xl font-black min-w-64 max-w- px-4 tracking-wide mb-5"
                  size="large"
                  :loading="!!connectionCheckingLoading[gameKey]"
                  @click="onConnectGameBtnClick(gameKey)"
                >
                  {{ t('loggedBox.connectGame') }}
                </el-button>
              </div>
            </template>
          </goat-page-home-exclusive-offer-tile>
        </div>
      </transition>
      <img
        alt="separator"
        class="w-full absolute lef-0 right-0 bottom-0 translate-y-1/2 z-10"
        :src="SeperatorImg"
      >
    </div>
    <goat-page-home-connect-game-dialog />
    <el-dialog
      v-model="benefitDialogVisibility"
      class="p-0 bg-transparent"
      :show-close="true"
      width="min(95vw, 800px)"
      center
    >
      <goat-site-benefits modal-mode />
    </el-dialog>
  </div>
</template>

<style scoped lang="postcss">
#logged-box :deep(>div) {
  .el-button {
    --el-button-size: 3em;
  }
}

.status-silver,
.status-SILVER {
    color: #818181;
  }

.status-diamond,
.status-DIAMOND {
    color: #18aeec;
  }

.status-gold,
.status-GOLD {
    color: #ffa800;
  }

.dark :deep(.el-progress-bar__outer) {
  background-color: white;
  border-block: 1px solid #62656a;
  border-inline: 1px solid #62656a;
}

.dark :deep(.el-progress-bar__inner) {
  z-index: 10;
  background: linear-gradient(to bottom, white, currentcolor) !important;
  background-color: currentcolor !important;
  border-radius: 100px 0 0 100px !important;
}
</style>
